// ═══════════════════════════════════════════════════════════════
// IMPORT SERVICE — Spektrix CSV Import
// Path: src/services/importService.js
//
// Parses Spektrix CSV exports, deduplicates donors by
// Facilitator Id, and imports donors + gift transactions.
//
// Blueprint: CMM_Spektrix_Import_Blueprint_v1_0.md
// ═══════════════════════════════════════════════════════════════

import { supabase } from "../lib/supabase";
import { debugLog } from "../utils/debugLog";

// ── Spektrix CSV column → CRM field mapping ─────────────────────
const COLUMN_MAP = {
  "facilitator id": { field: "spektrixId", table: "donor" },
  "owner first name": { field: "firstName", table: "donor" },
  "owner last name": { field: "lastName", table: "donor" },
  "owner name": { field: "credentialedFullName", table: "donor" },
  "owner email address": { field: "email", table: "donor" },
  "owner phone number": { field: "phone", table: "donor" },
  "owner cell phone number": { field: "cellPhone", table: "donor" },
  "organization": { field: "organization", table: "donor" },
  "contactaddress: line1": { field: "homeStreet", table: "donor" },
  "contactaddress: city": { field: "homeCity", table: "donor" },
  "contactaddress: state": { field: "homeState", table: "donor" },
  "contactaddress: zip code": { field: "homeZip", table: "donor" },
  "amount": { field: "amount", table: "gift" },
  "date confirmed": { field: "giftDate", table: "gift" },
  "fund name": { field: "fundName", table: "gift" },
  "fund code": { field: "fundCode", table: "gift" },
  "is anonymous": { field: "isAnonymous", table: "gift" },
  "recognition name": { field: "recognitionName", table: "gift" },
  "tribute name": { field: "tributeName", table: "gift" },
  "tribute type": { field: "tributeType", table: "gift" },
};

// ── Parse CSV text → array of row objects ───────────────────────
export function parseCSV(text) {
  // Strip BOM
  const clean = text.replace(/^\uFEFF/, "");
  const lines = clean.split(/\r?\n/).filter((line) => line.trim());
  if (lines.length < 2) return { headers: [], rows: [] };

  const headers = parseLine(lines[0]);
  const rows = [];

  for (let i = 1; i < lines.length; i++) {
    const values = parseLine(lines[i]);
    if (values.length === 0) continue;

    const row = {};
    headers.forEach((header, idx) => {
      row[header.trim()] = (values[idx] || "").trim();
    });
    rows.push(row);
  }

  return { headers, rows };
}

function parseLine(line) {
  const result = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if ((char === "," || char === "\t") && !inQuotes) {
      result.push(current);
      current = "";
    } else {
      current += char;
    }
  }
  result.push(current);
  return result;
}

// ── Auto-map CSV headers to CRM fields ──────────────────────────
export function autoMapColumns(headers) {
  const mapping = {};
  headers.forEach((header) => {
    const key = header.toLowerCase().trim();
    if (COLUMN_MAP[key]) {
      mapping[header] = COLUMN_MAP[key];
    }
  });
  return mapping;
}

// ── Parse date from MM/DD/YYYY → YYYY-MM-DD ─────────────────────
export function parseDate(dateStr) {
  if (!dateStr) return null;
  // Handle MM/DD/YYYY
  const parts = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (parts) {
    return `${parts[3]}-${parts[1].padStart(2, "0")}-${parts[2].padStart(2, "0")}`;
  }
  // Handle YYYY-MM-DD already
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  // Try native parse as fallback
  const d = new Date(dateStr);
  if (!isNaN(d)) return d.toISOString().slice(0, 10);
  return null;
}

// ── Check if a value is truthy (for Is Anonymous) ────────────────
function isTruthy(val) {
  if (!val) return false;
  const v = val.toString().toLowerCase().trim();
  return ["yes", "true", "1", "y"].includes(v);
}

// ── Analyze import: group by SpektrixID, count new vs existing ──
export async function analyzeImport(rows, mapping) {
  // Extract mapped data from each row
  const mapped = rows.map((row) => {
    const donor = {};
    const gift = {};
    Object.entries(mapping).forEach(([csvCol, { field, table }]) => {
      const val = row[csvCol] || "";
      if (table === "donor") donor[field] = val;
      else gift[field] = val;
    });
    return { donor, gift };
  });

  // Group by SpektrixID
  const groups = {};
  mapped.forEach((item) => {
    const sid = item.donor.spektrixId;
    if (!sid) return;
    if (!groups[sid]) {
      groups[sid] = { donor: item.donor, gifts: [] };
    }
    // Update donor fields with latest non-empty values
    Object.entries(item.donor).forEach(([key, val]) => {
      if (val && val.trim()) groups[sid].donor[key] = val.trim();
    });
    groups[sid].gifts.push(item.gift);
  });

  const uniqueDonors = Object.keys(groups);
  const totalGifts = mapped.filter((m) => m.donor.spektrixId).length;

  // Check which donors already exist
  let existingCount = 0;
  if (uniqueDonors.length > 0) {
    // Check in chunks of 100
    for (let i = 0; i < uniqueDonors.length; i += 100) {
      const chunk = uniqueDonors.slice(i, i + 100);
      const { data } = await supabase
        .from("members")
        .select("spektrix_id")
        .in("spektrix_id", chunk);
      if (data) existingCount += data.length;
    }
  }

  // Date range
  const dates = mapped
    .map((m) => parseDate(m.gift.giftDate))
    .filter(Boolean)
    .sort();

  // Unique funds
  const fundSet = new Set();
  mapped.forEach((m) => {
    if (m.gift.fundName) fundSet.add(m.gift.fundName);
  });

  const skippedNoId = rows.length - mapped.filter((m) => m.donor.spektrixId).length;

  return {
    totalRows: rows.length,
    uniqueDonors: uniqueDonors.length,
    newDonors: uniqueDonors.length - existingCount,
    existingDonors: existingCount,
    totalGifts,
    skippedNoId,
    dateRange: dates.length > 0 ? { earliest: dates[0], latest: dates[dates.length - 1] } : null,
    uniqueFunds: Array.from(fundSet).sort(),
    groups,
  };
}

// ── Filter analysis groups by date range ──────────────────────
// Returns a new analysis object with only gifts in the date range
export function filterAnalysisByDate(analysis, fromDate, toDate) {
  const { groups } = analysis;
  const filteredGroups = {};
  let totalGifts = 0;
  const fundSet = new Set();

  Object.entries(groups).forEach(([spektrixId, group]) => {
    const filteredGifts = group.gifts.filter((gift) => {
      const date = parseDate((gift.giftDate || "").trim());
      if (!date) return false;
      if (fromDate && date < fromDate) return false;
      if (toDate && date > toDate) return false;
      return true;
    });

    if (filteredGifts.length > 0) {
      filteredGroups[spektrixId] = {
        donor: group.donor,
        gifts: filteredGifts,
      };
      totalGifts += filteredGifts.length;
      filteredGifts.forEach((g) => {
        if (g.fundName) fundSet.add(g.fundName);
      });
    }
  });

  const uniqueDonors = Object.keys(filteredGroups);
  const dates = [];
  Object.values(filteredGroups).forEach((g) => {
    g.gifts.forEach((gift) => {
      const d = parseDate((gift.giftDate || "").trim());
      if (d) dates.push(d);
    });
  });
  dates.sort();

  return {
    ...analysis,
    groups: filteredGroups,
    uniqueDonors: uniqueDonors.length,
    totalGifts,
    dateRange: dates.length > 0 ? { earliest: dates[0], latest: dates[dates.length - 1] } : null,
    uniqueFunds: Array.from(fundSet).sort(),
  };
}

// ── Diagnose skipped rows ─────────────────────────────────────
// Returns a breakdown of why rows would be skipped during import
export function diagnoseImport(rows, mapping) {
  const reasons = {
    noSpektrixId: [],
    noAmount: [],
    zeroAmount: [],
    noDate: [],
    badDate: [],
    valid: 0,
  };

  rows.forEach((row, idx) => {
    const mapped = {};
    Object.entries(mapping).forEach(([csvCol, { field, table }]) => {
      mapped[field] = (row[csvCol] || "").trim();
    });

    const sid = mapped.spektrixId;
    if (!sid) {
      reasons.noSpektrixId.push({ idx, row: idx + 2, sample: JSON.stringify(row).slice(0, 120) });
      return;
    }

    const rawAmount = (mapped.amount || "").replace(/[$,\s]/g, "");
    const amount = parseFloat(rawAmount);
    if (!rawAmount) {
      reasons.noAmount.push({ idx, row: idx + 2, raw: mapped.amount });
      return;
    }
    if (!amount || amount <= 0) {
      reasons.zeroAmount.push({ idx, row: idx + 2, raw: mapped.amount, parsed: amount });
      return;
    }

    const rawDate = (mapped.giftDate || "").trim();
    if (!rawDate) {
      reasons.noDate.push({ idx, row: idx + 2 });
      return;
    }
    const parsed = parseDate(rawDate);
    if (!parsed) {
      reasons.badDate.push({ idx, row: idx + 2, raw: rawDate });
      return;
    }

    reasons.valid++;
  });

  return reasons;
}

// ── Find duplicate gifts before import ────────────────────────
// Returns array of { rowIndex, spektrixId, donorName, giftDate, amount, fundName, existingGiftId }
export async function findDuplicateGifts(analysis) {
  const { groups } = analysis;
  const duplicates = [];
  let globalRowIndex = 0;

  for (const spektrixId of Object.keys(groups)) {
    const group = groups[spektrixId];
    const donorName =
      (group.donor.firstName || "") + " " + (group.donor.lastName || "");

    // Look up the member by spektrix_id
    const { data: existing } = await supabase
      .from("members")
      .select("id")
      .eq("spektrix_id", spektrixId)
      .limit(1);

    const memberId = existing && existing.length > 0 ? existing[0].id : null;

    for (const gift of group.gifts) {
      const amount = parseFloat((gift.amount || "").replace(/[$,\s]/g, ""));
      const giftDate = parseDate((gift.giftDate || "").trim());

      if (memberId && amount > 0 && giftDate) {
        const { data: dup } = await supabase
          .from("gifts")
          .select("id, gift_date")
          .eq("member_id", memberId)
          .eq("gift_date", giftDate)
          .eq("amount", amount)
          .limit(1);

        if (dup && dup.length > 0) {
          duplicates.push({
            rowIndex: globalRowIndex,
            spektrixId,
            donorName: donorName.trim(),
            giftDate,
            amount,
            fundName: gift.fundName || "",
            existingGiftId: dup[0].id,
          });
        }
      }
      globalRowIndex++;
    }
  }

  debugLog.data("findDuplicateGifts", {
    total: globalRowIndex,
    duplicates: duplicates.length,
  });

  return duplicates;
}

// ── Execute the full import ─────────────────────────────────────
export async function executeImport(analysis, options, onProgress) {
  const { groups } = analysis;
  const donorIds = Object.keys(groups);
  const chapterId = options.chapterId;
  const userId = options.userId;
  const skipIndices = options.skipIndices || new Set();
  const defaultPathway = options.defaultPathway || "unengaged";
  const batchId = options.batchId || null;

  let donorsCreated = 0;
  let donorsUpdated = 0;
  let donorsFailedLookup = 0;
  let donorsSkipped = 0;
  let giftsCreated = 0;
  let giftsSkipped = 0;
  let errors = [];

  // Pre-load / auto-create funds
  const fundMap = await ensureFunds(analysis.uniqueFunds, chapterId);

  const total = donorIds.length;
  let globalGiftIndex = 0;

  for (let i = 0; i < donorIds.length; i++) {
    const spektrixId = donorIds[i];
    const group = groups[spektrixId];
    const donorData = group.donor;

    try {
      // ── Upsert donor ────────────────────────────────────
      let memberId;

      // Check if exists
      const { data: existing } = await supabase
        .from("members")
        .select("id")
        .eq("spektrix_id", spektrixId)
        .limit(1);

      if (existing && existing.length > 0) {
        memberId = existing[0].id;

        if (options.donorMode === "skip_existing") {
          donorsSkipped++;
        } else {
          // Update with non-empty fields
          const updateRow = buildDonorRow(donorData);
          if (Object.keys(updateRow).length > 0) {
            await supabase
              .from("members")
              .update(updateRow)
              .eq("id", memberId);
            donorsUpdated++;
          } else {
            donorsSkipped++;
          }
        }
      } else {
        if (options.donorMode === "update_only") {
          donorsSkipped++;
          // Still need memberId for gifts — skip gift indices too
          globalGiftIndex += group.gifts.length;
          continue;
        }

        // Create new donor
        const insertRow = {
          ...buildDonorRow(donorData),
          spektrix_id: spektrixId,
          pathway: defaultPathway,
          is_donor: true,
        };

        const { data: inserted, error: insertErr } = await supabase
          .from("members")
          .insert(insertRow)
          .select("id");

        if (insertErr) {
          errors.push({
            row: i + 1,
            spektrixId,
            error: "Donor insert: " + insertErr.message,
          });
          globalGiftIndex += group.gifts.length;
          continue;
        }

        memberId = inserted?.[0]?.id;
        donorsCreated++;
      }

      if (!memberId) {
        donorsFailedLookup++;
        debugLog.warn("importService", `No memberId for ${spektrixId} — skipping ${group.gifts.length} gifts`);
        globalGiftIndex += group.gifts.length;
        giftsSkipped += group.gifts.length;
        continue;
      }

      // ── Insert gifts ────────────────────────────────────
      for (const gift of group.gifts) {
        const amount = parseFloat((gift.amount || "").replace(/[$,\s]/g, ""));
        const giftDate = parseDate((gift.giftDate || "").trim());

        // Skip if user marked this row as duplicate in Step 4
        if (skipIndices.has(globalGiftIndex)) {
          giftsSkipped++;
          globalGiftIndex++;
          continue;
        }

        globalGiftIndex++;

        if (!amount || amount <= 0) {
          giftsSkipped++;
          debugLog.warn("importService", `Skipped gift: bad amount "${gift.amount}" for ${spektrixId}`);
          continue;
        }
        if (!giftDate) {
          giftsSkipped++;
          debugLog.warn("importService", `Skipped gift: bad date "${gift.giftDate}" for ${spektrixId}`);
          continue;
        }

        // Build notes from metadata
        const notes = buildGiftNotes(gift);

        // Resolve fund (normalized lookup — see ensureFunds)
        const fundId = gift.fundName
          ? fundMap[normalizeFundKey(gift.fundName)] || null
          : null;

        const { error: giftErr } = await supabase.from("gifts").insert({
          member_id: memberId,
          gift_type: "cash",
          amount,
          gift_date: giftDate,
          payment_method: "hist",
          fund_id: fundId,
          notes: notes || null,
          created_by: userId,
          batch_id: batchId,
        });

        if (giftErr) {
          errors.push({
            row: i + 1,
            spektrixId,
            error: "Gift insert: " + giftErr.message,
          });
        } else {
          giftsCreated++;
        }
      }
    } catch (err) {
      errors.push({
        row: i + 1,
        spektrixId,
        error: err.message,
      });
    }

    // Progress callback
    if (onProgress) {
      onProgress({
        current: i + 1,
        total,
        donorsCreated,
        donorsUpdated,
        giftsCreated,
      });
    }
  }

  debugLog.action("importService.executeImport", {
    donorsCreated,
    donorsUpdated,
    donorsSkipped,
    giftsCreated,
    giftsSkipped,
    errors: errors.length,
  });

  return {
    donorsCreated,
    donorsUpdated,
    donorsSkipped,
    donorsFailedLookup,
    giftsCreated,
    giftsSkipped,
    errors,
  };
}

// ── Build donor DB row from mapped fields ───────────────────────
function buildDonorRow(data) {
  const row = {};
  if (data.firstName?.trim()) row.first_name = data.firstName.trim();
  if (data.lastName?.trim()) row.last_name = data.lastName.trim();
  if (data.credentialedFullName?.trim())
    row.credentialed_full_name = data.credentialedFullName.trim();
  if (data.email?.trim()) row.email = data.email.trim();
  if (data.phone?.trim()) row.phone = data.phone.trim();
  if (data.cellPhone?.trim()) row.cell_phone = data.cellPhone.trim();
  if (data.organization?.trim()) row.organization = data.organization.trim();
  if (data.homeStreet?.trim()) row.home_street = data.homeStreet.trim();
  if (data.homeCity?.trim()) row.home_city = data.homeCity.trim();
  if (data.homeState?.trim()) row.home_state = data.homeState.trim();
  if (data.homeZip?.trim()) row.home_zip = data.homeZip.trim();
  return row;
}

// ── Build gift notes from metadata fields ───────────────────────
function buildGiftNotes(gift) {
  const parts = [];
  if (isTruthy(gift.isAnonymous)) parts.push("Anonymous");
  if (gift.recognitionName?.trim()) parts.push("Recognition: " + gift.recognitionName.trim());
  if (gift.tributeName?.trim()) {
    const type = gift.tributeType?.trim() || "Tribute";
    parts.push(`In ${type} of: ${gift.tributeName.trim()}`);
  }
  return parts.length > 0 ? parts.join(" | ") : null;
}

// ── Normalize a fund name for case/punctuation-insensitive matching
const normalizeFundKey = (name) =>
  (name || "").trim().toLowerCase().replace(/[^a-z0-9]/g, "");

// ── Ensure all funds exist, return name→id map ──────────────────
// Map is keyed by the normalized fund name so CSV variants like
// "pcpastrong", "#PCPASTRONG", and " PCPA Strong " all resolve to
// the same fund row. Existing funds are matched first; only truly
// new names create new fund rows.
async function ensureFunds(fundNames, chapterId) {
  const map = {};
  if (!fundNames || fundNames.length === 0) return map;

  // Load existing funds and key by normalized name
  const { data: existing } = await supabase
    .from("funds")
    .select("id, name, code");

  if (existing) {
    existing.forEach((f) => {
      map[normalizeFundKey(f.name)] = f.id;
      // Also index by code so a CSV value matching a fund code resolves
      if (f.code) map[normalizeFundKey(f.code)] = f.id;
    });
  }

  // Create missing funds
  for (const name of fundNames) {
    if (!name) continue;
    const key = normalizeFundKey(name);
    if (!key || map[key]) continue;

    const { data: created, error } = await supabase
      .from("funds")
      .insert({
        name,
        code: name
          .toUpperCase()
          .replace(/[^A-Z0-9]/g, "")
          .slice(0, 10),
        is_active: true,
        sort_order: 0,
      })
      .select("id");

    if (error) {
      debugLog.warn("ensureFunds insert failed", { name, error: error.message });
      continue;
    }
    if (created && created.length > 0) {
      map[key] = created[0].id;
      debugLog.data("Auto-created fund", { name });
    }
  }

  return map;
}
